import type { TemplateVersion } from '../types/sdm';

export function bumpMinor(oid: string): string {
  const match = oid.match(/^(.*?)(\d+)$/);
  if (!match) return oid + '.1';
  return match[1] + (parseInt(match[2]) + 1);
}

export function compareVersionOids(a: string | undefined, b: string | undefined): number {
  const segments = (v: string | undefined): number[] =>
    (v ?? '').match(/\d+/g)?.map(Number) ?? [0];
  const na = segments(a);
  const nb = segments(b);
  for (let i = 0; i < Math.max(na.length, nb.length); i++) {
    const diff = (na[i] ?? 0) - (nb[i] ?? 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

export function mergeVersions(base: TemplateVersion, delta: TemplateVersion): TemplateVersion {
  const mergeArr = <T extends { OID: string }>(b: T[], d: T[]): T[] => {
    const map = new Map((b ?? []).map(i => [i.OID, i]));
    (d ?? []).forEach(i => map.set(i.OID, i));
    return Array.from(map.values());
  };
  return {
    ...delta,
    ItemDef:      mergeArr(base.ItemDef,      delta.ItemDef),
    ItemGroupDef: mergeArr(base.ItemGroupDef, delta.ItemGroupDef),
    FormDef:      mergeArr(base.FormDef,      delta.FormDef),
    EventDef:     mergeArr(base.EventDef,     delta.EventDef),
    MethodDef:    mergeArr(base.MethodDef,    delta.MethodDef),
    CodeList:     mergeArr(base.CodeList,     delta.CodeList),
    ConditionDef: mergeArr(base.ConditionDef, delta.ConditionDef),
    Protocol: delta.Protocol?.EventRef?.length ? delta.Protocol : base.Protocol,
    Images:   delta.Images?.length            ? delta.Images   : base.Images,
  };
}

export function resolveVersionChain(versions: TemplateVersion[]): TemplateVersion | null {
  if (!versions || versions.length === 0) return null;
  const sorted = [...versions].sort((a, b) => compareVersionOids(a.version, b.version));
  return sorted.reduce((acc, v) => mergeVersions(acc, v));
}
